import { PACKAGE_PURCHASING_PERMISSION_LEVEL } from 'data/checkout';
import {
  MinimalMemberPackageResponseDto,
  MinimalMemberPackageResponseWithPlanDto,
} from 'utils/GeneratedApi';

type getPackagePurchasingBlockAndWarningMessageProps = {
  samePackagePurchasePermissionLevel: 'allow' | 'warn' | 'block' | undefined;
  multipleSubscriptionPlansPurchasePermissionLevel: 'allow' | 'warn' | 'block' | undefined;
  packagesWithDuplicateSlug: MinimalMemberPackageResponseDto[];
  packagesWithSubscriptionPlans: MinimalMemberPackageResponseWithPlanDto[];
  samePackagePurchaseAbilityWarningMessage: string;
  samePackagePurchaseAbilityBlockedMessage: string;
  multipleSubscriptionPlanPurchaseAbilityWarningMessage: string;
  multipleSubscriptionPlanPurchaseAbilityBlockedMessage: string;
  purchasabilityWarningMessageForAllCheck: string;
  purchasabilityBlockedMessageForAllCheck: string;
};

function getPackagePurchasingBlockAndWarningMessage({
  samePackagePurchasePermissionLevel,
  multipleSubscriptionPlansPurchasePermissionLevel,
  packagesWithDuplicateSlug,
  packagesWithSubscriptionPlans,
  samePackagePurchaseAbilityWarningMessage,
  samePackagePurchaseAbilityBlockedMessage,
  multipleSubscriptionPlanPurchaseAbilityWarningMessage,
  multipleSubscriptionPlanPurchaseAbilityBlockedMessage,
  purchasabilityWarningMessageForAllCheck,
  purchasabilityBlockedMessageForAllCheck,
}: getPackagePurchasingBlockAndWarningMessageProps) {
  let warningMessage = '';
  let blockedMessage = '';

  const hasDuplicatedPackages = Boolean(packagesWithDuplicateSlug?.length);
  const hasPackagesWithSubscriptionPlans = Boolean(packagesWithSubscriptionPlans?.length);

  switch (true) {
    // 1. If samePackagePurchasePermission is WARN and multipleSubscriptionPlansPurchasePermissionLevel is ALLOW show warning message for the first one, depending on the length of array
    case samePackagePurchasePermissionLevel === PACKAGE_PURCHASING_PERMISSION_LEVEL.WARN &&
      multipleSubscriptionPlansPurchasePermissionLevel ===
        PACKAGE_PURCHASING_PERMISSION_LEVEL.ALLOW:
      if (hasDuplicatedPackages) {
        warningMessage = samePackagePurchaseAbilityWarningMessage;
      }
      break;

    // 2. If samePackagePurchasePermission is ALLOW and multipleSubscriptionPlansPurchasePermissionLevel is WARN show warning message for the second one, depending on the length of array
    case samePackagePurchasePermissionLevel === PACKAGE_PURCHASING_PERMISSION_LEVEL.ALLOW &&
      multipleSubscriptionPlansPurchasePermissionLevel === PACKAGE_PURCHASING_PERMISSION_LEVEL.WARN:
      if (hasPackagesWithSubscriptionPlans) {
        warningMessage = multipleSubscriptionPlanPurchaseAbilityWarningMessage;
      }
      break;

    // 3. If samePackagePurchasePermission is BLOCK and multipleSubscriptionPlansPurchasePermissionLevel is ALLOW show BLOCKED message for the first one, depending on the length of array
    case samePackagePurchasePermissionLevel === PACKAGE_PURCHASING_PERMISSION_LEVEL.BLOCK &&
      multipleSubscriptionPlansPurchasePermissionLevel ===
        PACKAGE_PURCHASING_PERMISSION_LEVEL.ALLOW:
      if (hasDuplicatedPackages) {
        blockedMessage = samePackagePurchaseAbilityBlockedMessage;
      }
      break;

    // 4. If samePackagePurchasePermission is ALLOW and multipleSubscriptionPlansPurchasePermissionLevel is BLOCK show BLOCKED message for the second one, depending on the length of array
    case samePackagePurchasePermissionLevel === PACKAGE_PURCHASING_PERMISSION_LEVEL.ALLOW &&
      multipleSubscriptionPlansPurchasePermissionLevel ===
        PACKAGE_PURCHASING_PERMISSION_LEVEL.BLOCK:
      if (hasPackagesWithSubscriptionPlans) {
        blockedMessage = multipleSubscriptionPlanPurchaseAbilityBlockedMessage;
      }
      break;

    // 5. If samePackagePurchasePermission is WARN and multipleSubscriptionPlansPurchasePermissionLevel is BLOCK prioritize the the second one depending on the length of both array
    case samePackagePurchasePermissionLevel === PACKAGE_PURCHASING_PERMISSION_LEVEL.WARN &&
      multipleSubscriptionPlansPurchasePermissionLevel ===
        PACKAGE_PURCHASING_PERMISSION_LEVEL.BLOCK:
      if (hasPackagesWithSubscriptionPlans) {
        blockedMessage = multipleSubscriptionPlanPurchaseAbilityBlockedMessage;
      } else if (hasDuplicatedPackages) {
        warningMessage = samePackagePurchaseAbilityWarningMessage;
      }
      break;

    // 6. If samePackagePurchasePermission is BLOCK and multipleSubscriptionPlansPurchasePermissionLevel is WARN prioritize the the first one depending on the length of both array
    case samePackagePurchasePermissionLevel === PACKAGE_PURCHASING_PERMISSION_LEVEL.BLOCK &&
      multipleSubscriptionPlansPurchasePermissionLevel === PACKAGE_PURCHASING_PERMISSION_LEVEL.WARN:
      if (hasDuplicatedPackages) {
        blockedMessage = samePackagePurchaseAbilityBlockedMessage;
      } else if (hasPackagesWithSubscriptionPlans) {
        warningMessage = multipleSubscriptionPlanPurchaseAbilityWarningMessage;
      }
      break;

    // 7. If both the permission is WARN show warning message depending on the length of both array
    case samePackagePurchasePermissionLevel === PACKAGE_PURCHASING_PERMISSION_LEVEL.WARN &&
      multipleSubscriptionPlansPurchasePermissionLevel === PACKAGE_PURCHASING_PERMISSION_LEVEL.WARN:
      if (hasDuplicatedPackages && hasPackagesWithSubscriptionPlans) {
        warningMessage = purchasabilityWarningMessageForAllCheck;
      } else if (hasDuplicatedPackages && !hasPackagesWithSubscriptionPlans) {
        warningMessage = samePackagePurchaseAbilityBlockedMessage;
      } else if (!hasDuplicatedPackages && hasPackagesWithSubscriptionPlans) {
        warningMessage = multipleSubscriptionPlanPurchaseAbilityWarningMessage;
      }
      break;

    // 8. If both the permission is BLOCK show blocked message depending on the length of both array
    case samePackagePurchasePermissionLevel === PACKAGE_PURCHASING_PERMISSION_LEVEL.BLOCK &&
      multipleSubscriptionPlansPurchasePermissionLevel ===
        PACKAGE_PURCHASING_PERMISSION_LEVEL.BLOCK:
      if (hasDuplicatedPackages && hasPackagesWithSubscriptionPlans) {
        blockedMessage = purchasabilityBlockedMessageForAllCheck;
      } else if (hasDuplicatedPackages && !hasPackagesWithSubscriptionPlans) {
        blockedMessage = samePackagePurchaseAbilityBlockedMessage;
      } else if (!hasDuplicatedPackages && hasPackagesWithSubscriptionPlans) {
        blockedMessage = multipleSubscriptionPlanPurchaseAbilityBlockedMessage;
      }
      break;

    default:
      break;
  }

  return {
    warningMessage,
    blockedMessage,
  };
}

export default getPackagePurchasingBlockAndWarningMessage;
